import { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { of } from 'rxjs';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';

import { TranslatePipe } from '@recruitee/i18n';
import {
  ConnectedOverlayDirective,
  DialogRef,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  TypeaheadInlineComponent,
  TypeaheadSuggestionsComponent,
} from '@recruitee/user-interface';

import { Destroyable, HttpErrorHandlerService, takeUntilDestroyed } from '../../../common';
import { GfeaApiService } from '../gfea-api';
import type { Profession, ProfessionCategory } from '../types';
import { ProfessionType } from '../types';

export type ProfessionSelection = { id: string; value: string };

@Component({
  selector: 'rti-gfea-profession-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-profession-select.component.html',
  styleUrls: ['./gfea-profession-select.component.less'],
  imports: [
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    FormFieldComponent,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    TypeaheadInlineComponent,
    TypeaheadSuggestionsComponent,
    TranslatePipe,
  ],
})
@Destroyable()
export class GfeaProfessionSelectComponent {
  @Input() public currentSelection: ProfessionSelection | null = null;
  @Input() public professionCategory: ProfessionCategory | null = null;
  public professionSelected = output<ProfessionSelection | null>();
  public DropdownWidth = DropdownWidth;

  public dropdownPositions: ConnectionPositionPair[] = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
    ),
    new ConnectionPositionPair(
      { originX: 'start', originY: 'top' },
      { overlayX: 'start', overlayY: 'bottom' },
    ),
  ];

  public queryControl: UntypedFormControl = new UntypedFormControl(null);
  public suggestionsLoading: boolean = false;
  public suggestions: ProfessionSelection[] = [];

  constructor(
    public dialog: DialogRef,
    private gfeaApi: GfeaApiService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.queryControl.valueChanges
      .pipe(
        startWith(''),
        takeUntilDestroyed(this),
        tap(() => {
          this.suggestionsLoading = true;
          this.cdRef.detectChanges();
        }),
        debounceTime(200),
        switchMap((query: string) => {
          if (!this.professionCategory) return of({ professions: [] });

          return this.gfeaApi.get(
            `/profession/search?name=${query || ''}&category=${this.professionCategory}`,
          );
        }),
      )
      .subscribe(
        (result: { professions: Profession[] }) => {
          this.suggestions = result.professions.map((profession, index) => ({
            id: profession.remoteId,
            value: this.professionName(profession, index, result.professions),
          }));
          this.suggestionsLoading = false;
          this.cdRef.detectChanges();
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
          this.suggestionsLoading = false;
          this.cdRef.detectChanges();
        },
      );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('professionCategory' in changes) {
      this.queryControl.setValue('');
    }
  }

  private professionName(
    profession: Profession,
    professionIndex: number,
    allProfessions: Profession[],
  ): string {
    const previousName = allProfessions[professionIndex - 1]?.name;
    const nextName = allProfessions[professionIndex + 1]?.name;

    return profession.name === previousName || profession.name === nextName
      ? `${profession.name} (${this.professionTypeCopy(profession)})`
      : profession.name;
  }

  private professionTypeCopy(profession: Profession): string {
    return profession.type === ProfessionType.TRAINING
      ? 'Ausbildungsbezeichung'
      : 'Berufsbezeichnung';
  }
}
